import * as Sentry from "@sentry/react";
import RecruiterUser from "../apiResources/RecruiterUser";
import Feedback from "../apiResources/Feedback";
import Engagement from "../apiResources/Engagement";
import EngagementsAccept from "../apiResources/EngagementsAccept";
import EngagementsReject from "../apiResources/EngagementsReject";
import Employer from "../apiResources/Employer";
import Application from "../apiResources/Application";
import Job from "../apiResources/Job";
import ApplicantReview from "../apiResources/ApplicantReview";
import CurrentUser from "../apiResources/CurrentUser";
import companyPerformanceAdapter from "./adapters/agencyProfileAdapters/companyPerformanceAdapter";
import recruiterHiringHubScoreAdapter from "./adapters/agencyProfileAdapters/recruiterHiringHubScoreAdapter";
import recruiterUserAdapter from "./adapters/agencyProfileAdapters/recruiterUserAdapter";
import {
  updateAboutCompany,
  updateAboutMe,
  updateCandidateQualifiers,
  updateSpecialities,
  updateUser,
  updateUserPhoto,
  updateRecruiterProfileImage,
} from "./helpers/agencyProfileHelpers";
import {
  LOAD_AGENCY_PROFILE,
  LOAD_AGENCY_PROFILE_ERROR,
  LOAD_RECRUITER_REVIEWS,
  LOAD_APPLICANT_REVIEWS,
  LOAD_TEAM_MEMBERS,
  SET_USER_FORM_LOADING,
  SET_IMAGES_FORM_LOADING,
  SET_REVIEWS_LOADING,
  SUBMIT_UPDATE,
  SUBMIT_UPDATE_ERROR,
  UPDATE_AVATAR,
  UPDATE_PROFILE_IMAGE,
  UPDATE_USER_DATA,
  REJECT_ENGAGEMENT,
  REJECT_ENGAGEMENT_ERROR,
  ACCEPT_ENGAGEMENT,
  ACCEPT_ENGAGEMENT_ERROR,
  SET_REJECT_ENGAGEMENT_LOADING,
  SET_ACCEPT_ENGAGEMENT_LOADING,
} from "./types";

export const loadRecruiterReviews =
  (recruiterId, page, perPageCount) => async (dispatch) => {
    dispatch({
      type: SET_REVIEWS_LOADING,
    });

    try {
      const employerScope = Employer.where({
        employers_via_recruiter_reviews: recruiterId,
      });

      const employerReviews = await Feedback.where({ reviews_for: recruiterId })
        .order({ created_at: "desc" })
        .includes("employer")
        .merge({ employer: employerScope })
        .page(page)
        .per(perPageCount)
        .stats({ total: "count" })
        .all();

      const applicantReviews = await ApplicantReview.where({
        recruiter_id: recruiterId,
      })
        .order({ created_at: "desc" })
        .page(page)
        .per(perPageCount)
        .stats({ total: "count" })
        .all();

      dispatch({
        type: LOAD_RECRUITER_REVIEWS,
        payload: {
          employerReviews: employerReviews.data,
          employerReviewsTotal: employerReviews.meta.stats.total.count,
          employerAndApplicantReviewsTotal:
            employerReviews.meta.stats.total.count +
            applicantReviews.meta.stats.total.count,
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_AGENCY_PROFILE_ERROR,
      });
    }
  };

export const loadApplicantReviews =
  (recruiterId, page, perPageCount) => async (dispatch) => {
    dispatch({
      type: SET_REVIEWS_LOADING,
    });

    try {
      const applicantReviews = await ApplicantReview.where({
        recruiter_id: recruiterId,
      })
        .order({ created_at: "desc" })
        .page(page)
        .per(perPageCount)
        .stats({ total: "count" })
        .all();

      dispatch({
        type: LOAD_APPLICANT_REVIEWS,
        payload: {
          applicantReviews: applicantReviews.data,
          applicantReviewsTotal: applicantReviews.meta.stats.total.count,
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_AGENCY_PROFILE_ERROR,
      });
    }
  };

export const loadTeamMembers =
  (recruiterUserId, recruiterId) => async (dispatch) => {
    try {
      const teamMembers = await RecruiterUser.where({
        company_id: recruiterId,
      }).all();

      dispatch({
        type: LOAD_TEAM_MEMBERS,
        payload: {
          teamMembers: teamMembers.data.filter(
            (member) => member.id !== recruiterUserId
          ),
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_AGENCY_PROFILE_ERROR,
      });
    }
  };

export const loadAgencyProfile =
  ({ recruiterUserId, engagementId }) =>
  async (dispatch) => {
    try {
      const recruiterUser = await RecruiterUser.includes([
        "speciality",
        "candidate_qualifier",
        {
          recruiter: [
            "recruiter_users",
            "recruiter_job_metrics",
            "recruiter_hiring_hub_score",
            "active_subscription",
          ],
        },
      ]).find(recruiterUserId);

      const employerPendingEngagement = engagementId
        ? await Engagement.find(engagementId)
        : null;

      const currentUser = await CurrentUser.first();

      const {
        data: { recruiter },
      } = recruiterUser;
      const companyPerformance = companyPerformanceAdapter(recruiter);
      const recruiterUserData = recruiterUserAdapter(recruiterUser.data);

      const jobScope = Job.where({
        expose_jobs_with_other_employers: recruiterUser.data.companyId,
      });

      const engagementHistory = await Engagement.where({
        expose_engagements_with_other_employers: recruiterUser.data.companyId,
        include_deleted: true,
        status: ["pending", "accepted"],
        job_status: ["new", "open", "interview_stage", "offer_made"],
      })
        .order({ created_at: "desc" })
        .includes("job")
        .merge({ job: jobScope })
        .per(10)
        .all();

      const placementHistory = await Application.where({
        expose_applications_with_other_employers: recruiterUser.data.companyId,
        status: "accepted_job",
      })
        .order({ created_at: "desc" })
        .includes("job")
        .merge({ job: jobScope })
        .per(10)
        .all();

      const recruiterHHScore = recruiterHiringHubScoreAdapter(recruiter);

      dispatch({
        type: LOAD_AGENCY_PROFILE,
        payload: {
          slides: [
            {
              id: 0,
              url: recruiter.profileImageUrl,
            },
          ],
          user: recruiterUserData,
          currentUser: currentUser.data,
          hhScore: recruiterHHScore,
          applicantReviews: [],
          applicantReviewsTotal: 0,
          companyPerformance,
          companySpecialities: recruiterUser.data.speciality.description,
          companyEngagementHistory: engagementHistory.data,
          companyPlacementHistory: placementHistory.data,
          qualificationTechniques: recruiterUser.data.candidateQualifier.list,
          aboutAgent: recruiterUser.data.biography,
          teamMembers: recruiterUser.data.recruiter.recruiterUsers.filter(
            (member) => member.id !== recruiterUser.data.id
          ),
          engagement: employerPendingEngagement?.data,
          aboutCompany: recruiterUser.data.recruiter.profile,
          profileImageUrl: recruiterUser.data.recruiter.profileImageUrl,
          employerReviews: [],
          employerReviewsTotal: 0,
          employerAndApplicantReviewsTotal: 0,
          engagementMessageData: employerPendingEngagement?.data && {
            agentName: recruiterUser.data.firstName,
            agencyName: recruiterUser.data.recruiter.name,
            message: employerPendingEngagement.data.message,
          },
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_AGENCY_PROFILE_ERROR,
      });
    }
  };

export const submitUpdate = (data, closeForm, formName) => async (dispatch) => {
  dispatch({ type: `SET_${formName}_FORM_LOADING` });

  try {
    switch (formName) {
      case "USER": {
        const user = await updateUser(data);

        dispatch({
          type: UPDATE_USER_DATA,
          payload: {
            firstName: user.data.firstName,
            lastName: user.data.lastName,
            location: user.data.location,
            yearsExperience: user.data.yearsExperience,
            phone: user.data.mobileNumber,
            linkedin: user.data.linkedinUrl,
            companyName: user.data.recruiter.name,
            website: user.data.recruiter.websiteAddress,
          },
        });

        break;
      }
      case "SPECIALITIES": {
        await updateSpecialities(data);

        dispatch({
          type: SUBMIT_UPDATE,
          payload: data,
        });
        break;
      }
      case "ABOUT_COMPANY": {
        await updateAboutCompany(data);

        dispatch({
          type: SUBMIT_UPDATE,
          payload: data,
        });
        break;
      }
      case "ABOUT_ME": {
        await updateAboutMe(data);

        dispatch({
          type: SUBMIT_UPDATE,
          payload: data,
        });
        break;
      }
      case "QUALIFICATIONS": {
        await updateCandidateQualifiers(data);

        dispatch({
          type: SUBMIT_UPDATE,
          payload: data,
        });
        break;
      }
      case "UPLOAD_AVATAR": {
        dispatch({ type: SET_USER_FORM_LOADING });
        const user = await updateUserPhoto(data);

        dispatch({
          type: UPDATE_AVATAR,
          payload: { avatar: user.data.avatar },
        });
        break;
      }
      case "PROFILE_IMAGE": {
        dispatch({ type: SET_IMAGES_FORM_LOADING });
        const user = await updateRecruiterProfileImage(data);

        dispatch({
          type: UPDATE_PROFILE_IMAGE,
          payload: { profileImageUrl: user.data.recruiter.profileImageUrl },
        });
        break;
      }
      default:
        break;
    }

    closeForm();
  } catch (error) {
    Sentry.captureException(error);

    dispatch({
      type: SUBMIT_UPDATE_ERROR,
      payload: error,
    });
  }
};

export const previewAvatar = (data) => (dispatch) => {
  dispatch({
    type: UPDATE_AVATAR,
    payload: { avatar: data.avatar, oldAvatar: data.oldAvatar },
  });
};

const rejectEngagement = async ({
  engagementId,
  employerUserId,
  rejectMessage,
}) => {
  const engagement = new EngagementsReject({
    engagementId,
    employerUserId,
    rejectedMessage: rejectMessage,
  });

  await engagement.save();

  return engagement;
};

const redirectUserToAgencies = ({ jobId }) => {
  const backToQuery = new URLSearchParams(window.location.search).get(
    "back_to"
  );
  const defaultRedirect = `/employer/jobs/${jobId}/applications#/agents`;

  window.location.assign(backToQuery || defaultRedirect);
};

export const submitRejectEngagement = (data) => async (dispatch) => {
  dispatch({
    type: SET_REJECT_ENGAGEMENT_LOADING,
  });

  try {
    const engagement = await rejectEngagement(data);

    dispatch({
      type: REJECT_ENGAGEMENT,
      payload: {
        status: engagement.data.status,
        respondedAt: engagement.data.respondedAt,
        rejectedMessage: engagement.data.rejectedMessage,
      },
    });
  } catch (error) {
    dispatch({
      type: REJECT_ENGAGEMENT_ERROR,
      payload: error,
    });
  }

  redirectUserToAgencies(data);
};

const acceptEngagement = async ({ engagementId, employerUserId }) => {
  const engagementsAccept = new EngagementsAccept({
    engagementId,
    employerUserId,
  });

  await engagementsAccept.save();

  return engagementsAccept;
};

export const submitAcceptEngagement = (data) => async (dispatch) => {
  dispatch({
    type: SET_ACCEPT_ENGAGEMENT_LOADING,
  });

  try {
    const engagement = await acceptEngagement(data);

    dispatch({
      type: ACCEPT_ENGAGEMENT,
      payload: {
        status: engagement.data.status,
        respondedAt: engagement.data.respondedAt,
      },
    });
  } catch (error) {
    Sentry.captureException(error);

    dispatch({
      type: ACCEPT_ENGAGEMENT_ERROR,
      payload: error,
    });
  }

  redirectUserToAgencies(data);
};
